import { ref, onUnmounted } from 'vue'

export type AsyncJobStatus = 'idle' | 'pending' | 'complete' | 'failed'

/**
 * Generic composable for polling an async background job until it completes.
 *
 * Usage:
 *   const job = useAsyncJob((jid) => adminJobStatus.show({ params: { jid }, visit: false }))
 *   await job.start(jid)
 *   // job.status, job.result, job.error are reactive
 */
export function useAsyncJob (
  pollFn: (jid: string) => Promise<{ status: string; result?: string | null; error?: string | null }>,
  intervalMs = 2000,
) {
  const status = ref<AsyncJobStatus>('idle')
  const result = ref<string | null>(null)
  const error = ref<string | null>(null)

  let timerId: ReturnType<typeof setTimeout> | null = null

  function stop () {
    if (timerId !== null) {
      clearTimeout(timerId)
      timerId = null
    }
  }

  function reset () {
    stop()
    status.value = 'idle'
    result.value = null
    error.value = null
  }

  async function tick (jid: string) {
    try {
      const data = await pollFn(jid)

      if (data.status === 'complete') {
        status.value = 'complete'
        result.value = data.result ?? null
        return
      }

      if (data.status === 'failed' || data.status === 'not_found') {
        status.value = 'failed'
        error.value = data.error ?? (data.status === 'not_found'
          ? 'Job not found'
          : 'An error occurred in the background job'
        )
        return
      }

      // still pending — schedule the next poll
      timerId = setTimeout(() => tick(jid), intervalMs)
    }
    catch (e: any) {
      status.value = 'failed'
      error.value = e?.message ?? 'Polling failed'
    }
  }

  function start (jid: string) {
    reset()
    status.value = 'pending'
    timerId = setTimeout(() => tick(jid), intervalMs)
  }

  onUnmounted(stop)

  return { status, result, error, start, reset }
}
